import { NetworkStatusPopoverBody } from '@/components/network-status/NetworkStatusPopover'
import { type NetworkQuality, type NetworkVerdict, networkVerdict } from '@/components/network-status/networkVerdict'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useRefreshingNow } from '@/hooks/useRefreshingNow'
import { useDiagnosticsStore } from '@/stores/diagnosticsStore'
import { type LucideIcon, SignalHigh, SignalLow, SignalMedium, Wifi, WifiOff } from 'lucide-react'
import { type PointerEvent, useMemo, useState } from 'react'

// The glyph carries the level so it survives greyscale and colour blindness; colour only
// reinforces it. 'unknown' means connected but unmeasured, so it gets a plain wifi mark: empty
// bars read as a broken pill, and a spinner reads as work that never finishes.
const QUALITY_ICON: Record<NetworkQuality, LucideIcon> = {
	unknown: Wifi,
	good: SignalHigh,
	fair: SignalMedium,
	poor: SignalLow,
	offline: WifiOff,
}

const QUALITY_COLOR: Record<NetworkQuality, string> = {
	unknown: 'text-muted-foreground',
	good: 'text-success',
	fair: 'text-warning',
	poor: 'text-destructive',
	offline: 'text-muted-foreground',
}

const QUALITY_TITLE: Record<NetworkQuality, string> = {
	unknown: 'Connected, network latency not measured',
	good: 'Network latency good',
	fair: 'Network latency fair',
	poor: 'Network latency poor',
	offline: 'No connection',
}

// Display freshness only, not part of any measurement.
const DISPLAY_REFRESH_MS = 30_000

// The only state worth spelling out: a fact rather than an inference.
const pillLabel = ({ quality }: NetworkVerdict): string | undefined => (quality === 'offline' ? 'offline' : undefined)

export const NetworkStatus = () => {
	const [open, setOpen] = useState(false)
	const metrics = useDiagnosticsStore(s => s.metrics)
	const online = useOnlineStatus()
	const now = useRefreshingNow(DISPLAY_REFRESH_MS)

	const verdict = useMemo(() => networkVerdict(metrics, online, now), [metrics, online, now])

	const Icon = QUALITY_ICON[verdict.quality]
	const label = pillLabel(verdict)

	// Touch has no hover. Mouse is left alone: toggling on mouse down would close the card the
	// moment someone clicked the pill they were already hovering.
	const toggleOnTouch = (event: PointerEvent<HTMLButtonElement>) => {
		if (event.pointerType !== 'mouse') {
			setOpen(current => !current)
		}
	}

	return (
		<div className='flex justify-end px-3 py-2'>
			{/* A button, not the default anchor, so focus opens the card too. */}
			<HoverCard open={open} onOpenChange={setOpen} openDelay={150} closeDelay={200}>
				<HoverCardTrigger asChild>
					<button
						type='button'
						data-testid='network-status-trigger'
						onPointerDown={toggleOnTouch}
						title={QUALITY_TITLE[verdict.quality]}
						className='inline-flex items-center gap-1.5 rounded-full border bg-card px-2 py-1 shadow-levitating transition-colors hover:bg-sidebar-accent cursor-pointer'>
						<span
							data-testid='network-status-icon'
							data-quality={verdict.quality}
							className={`flex items-center transition-colors ${QUALITY_COLOR[verdict.quality]}`}>
							<Icon className='size-(--icon-standalone)' aria-hidden />
						</span>
						{label ? (
							<span
								data-testid='network-status-badge'
								className='text-control font-medium motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95'>
								{label}
							</span>
						) : null}
					</button>
				</HoverCardTrigger>
				<HoverCardContent
					align='start'
					side='top'
					data-testid='network-status-popover'
					className='w-72 z-100'
					onPointerDownOutside={() => setOpen(false)}
					onEscapeKeyDown={() => setOpen(false)}>
					<NetworkStatusPopoverBody verdict={verdict} />
				</HoverCardContent>
			</HoverCard>
		</div>
	)
}
